/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import AccordionWrapper from '@ion/dls/src/components/accordion/Accordion';
import {AccordionSectionHeader} from '@ion/dls/src/components/accordion/current/AccordionSectionHeader';
import {getAccordionTopBorderStyle} from './assetDetailsAccordionStyles';
import {MissingCountBanner} from './MissingCountBanner';
import {TagsTabKey} from './TagsCoachingText';
import {TagsEmptyState} from './TagsEmptyState';
import {TagsTabBar} from './TagsTabBar';
import {TagTitleList, TagTitleRowData} from './TagTitleList';

export interface TagsAccordionProps {
  infoTooltip?: string;
  activeTab: TagsTabKey;
  onTabChange: (tab: TagsTabKey) => void;
  assignedRows: TagTitleRowData[];
  nearbyRows: TagTitleRowData[];
  expanded?: boolean;
  onExpand?: (expanded: boolean) => void;
}

const useStyles = makeStyles(() => ({
  // every Asset Details Drawer accordion gets this 2px teal top border to
  // separate it from the card above it ...
  header : getAccordionTopBorderStyle(),
  // padding now comes from AccordionWrapper's own detailsRoot (current
  // theme default: 8px 16px 24px 16px) rather than being re-applied here ...
  body   : {
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    width         : '100%',
    gap           : '16px'
  }
}));

/**
 * TagsAccordion renders the "TAGS" accordion section of the Asset Details
 * Drawer (ION-7463) - the accordion shell (title, info icon, total-count
 * badge, chevron), the Assigned/In Range tab bar, and the active tab's
 * content (a missing-tag banner and tag list, or an empty-state message).
 * The tab bar's own coaching sentence (e.g. "All tags currently assigned
 * to this asset.") is only shown when the active tab actually has rows -
 * it's redundant once `TagsEmptyState`'s own message is already showing.
 *
 * ```typescript
 *
 *   import {TagsAccordion} from
 *     '@ion/dls/src/components/assetdetails/current/TagsAccordion';
 *   ...
 *
 *   <TagsAccordion infoTooltip='Tags assigned to or detected near this asset.'
 *     activeTab='assigned'
 *     onTabChange={onTabChange}
 *     assignedRows={[
 *       {id: '1', assetType: 'tool', assetName: 'JacksBag', range: 'in'},
 *       {id: '2', assetType: 'trailer', assetName: 'Trailer', range: 'out'}
 *     ]}
 *     nearbyRows={[]} />
 *
 * ```
 *
 */
export const TagsAccordion = ({
  infoTooltip,
  activeTab,
  onTabChange,
  assignedRows,
  nearbyRows,
  expanded,
  onExpand
}: TagsAccordionProps) => {
  const classes = useStyles();

  const outOfRangeAssignedCount = useMemo(() => (
    assignedRows.filter(row => row.range === 'out').length
  ), [assignedRows]);

  const hasAssignedAlert = outOfRangeAssignedCount > 0;
  // every distinct tag once - an assigned tag in range is listed in both
  // tabs, and was counted twice in the title ...
  const totalCount = useMemo(() => (
    new Set([...assignedRows, ...nearbyRows].map(row => row.id)).size
  ), [assignedRows, nearbyRows]);
  const activeRows       = activeTab === 'assigned' ? assignedRows : nearbyRows;
  const isEmpty          = activeRows.length === 0;

  const header = (
    <AccordionSectionHeader title='TAGS'
      count={totalCount}
      infoTooltip={infoTooltip} />
  );

  const details = (
    <div className={classes.body}>
      <TagsTabBar activeTab={activeTab}
        onTabChange={onTabChange}
        assignedCount={assignedRows.length}
        nearbyCount={nearbyRows.length}
        hasAssignedAlert={hasAssignedAlert}
        showCoachingText={!isEmpty} />
      {isEmpty
        ? <TagsEmptyState activeTab={activeTab} />
        : (
          <>
            {activeTab === 'assigned' && hasAssignedAlert && (
              <MissingCountBanner missingCount={outOfRangeAssignedCount}
                totalCount={assignedRows.length} />
            )}
            <TagTitleList context={activeTab} rows={activeRows} />
          </>
        )}
    </div>
  );

  return (
    <AccordionWrapper isCustomStyle
      className={classes.header}
      expanded={expanded}
      onExpand={onExpand}
      header={header}
      details={details} />
  );
};

export default memo(TagsAccordion);
